import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import emailNotificationService from '../../services/emailNotificationService';
import { rbacService } from '../../services/rbacService';
import complianceReportsCustomizationService from '../../services/complianceReportsCustomizationService';
import AutomatedEmailTriggers from './components/AutomatedEmailTriggers';

const EmailNotificationSettings = () => {
  const [activeTab, setActiveTab] = useState('stakeholders');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Stakeholder Management
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [stakeholderEmail, setStakeholderEmail] = useState('');
  const [stakeholderDepartment, setStakeholderDepartment] = useState('');

  // Scheduled Reports
  const [schedules, setSchedules] = useState([]);
  const [newSchedule, setNewSchedule] = useState({
    scheduleName: '',
    deliveryFrequency: 'weekly',
    recipientEmails: '',
    recipientDepartments: '',
    reportFormat: 'pdf',
    isActive: true
  });

  // Test Email
  const [testEmail, setTestEmail] = useState('');
  const [testType, setTestType] = useState('deadline');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rolesData, schedulesData] = await Promise.all([
        rbacService?.getAllRoles(),
        complianceReportsCustomizationService?.getAllScheduledDeliveries()
      ]);
      setRoles(rolesData || []);
      setSchedules(schedulesData || []);
    } catch (err) {
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStakeholder = async () => {
    try {
      if (!stakeholderEmail || !selectedRole) {
        setError('Please provide email and role');
        return;
      }

      setLoading(true);
      setError('');
      setSuccess('');

      await rbacService?.assignRoleToUser({
        userEmail: stakeholderEmail,
        roleId: selectedRole,
        department: stakeholderDepartment || null,
        assignedBy: 'system'
      });

      setSuccess('Stakeholder added successfully');
      setStakeholderEmail('');
      setStakeholderDepartment('');
      setSelectedRole('');
    } catch (err) {
      setError(err?.message || 'Failed to add stakeholder');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSchedule = async () => {
    try {
      if (!newSchedule?.scheduleName || !newSchedule?.recipientEmails) {
        setError('Please provide schedule name and at least one recipient email');
        return;
      }

      setLoading(true);
      setError('');
      setSuccess('');

      const recipientEmailsArray = newSchedule?.recipientEmails
        ?.split(',')
        ?.map(e => e?.trim())
        ?.filter(e => e);

      const recipientDepartmentsArray = newSchedule?.recipientDepartments
        ?.split(',')
        ?.map(d => d?.trim())
        ?.filter(d => d);

      await complianceReportsCustomizationService?.createScheduledDelivery({
        scheduleName: newSchedule?.scheduleName,
        deliveryFrequency: newSchedule?.deliveryFrequency,
        recipientEmails: recipientEmailsArray,
        recipientDepartments: recipientDepartmentsArray,
        reportFormat: newSchedule?.reportFormat,
        isActive: newSchedule?.isActive,
        createdBy: 'system'
      });

      setSuccess('Scheduled report created successfully');
      setNewSchedule({
        scheduleName: '',
        deliveryFrequency: 'weekly',
        recipientEmails: '',
        recipientDepartments: '',
        reportFormat: 'pdf',
        isActive: true
      });
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to create schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    try {
      if (!testEmail) {
        setError('Please provide a test email address');
        return;
      }

      setLoading(true);
      setError('');
      setSuccess('');

      if (testType === 'deadline') {
        await emailNotificationService?.sendComplianceDeadlineAlert([testEmail], {
          title: 'Test Compliance Deadline',
          type: 'nasfund_deadline',
          dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)?.toISOString(),
          severity: 'high',
          affectedCount: 10,
          message: 'This is a test deadline alert to verify email delivery.'
        });
      } else {
        await emailNotificationService?.sendViolationReport(
          [testEmail],
          [
            {
              violationType: 'minimum_wage_violation',
              severity: 'critical',
              department: 'Operations',
              affectedCount: 5,
              estimatedCost: 2500.00
            },
            {
              violationType: 'overtime_calculation_error',
              severity: 'high',
              department: 'Manufacturing',
              affectedCount: 8,
              estimatedCost: 1200.00
            }
          ],
          'Test Report - ' + new Date()?.toLocaleDateString('en-PG')
        );
      }

      setSuccess(`Test ${testType === 'deadline' ? 'deadline alert' : 'violation report'} sent successfully to ${testEmail}`);
      setTestEmail('');
    } catch (err) {
      setError(err?.message || 'Failed to send test email');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSchedule = async (scheduleId, currentStatus) => {
    try {
      await complianceReportsCustomizationService?.updateScheduledDelivery(scheduleId, {
        isActive: !currentStatus
      });
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to update schedule');
    }
  };

  const departments = ['Operations', 'Finance', 'HR', 'IT', 'Manufacturing', 'Sales', 'Marketing'];

  return (
    <div className="min-h-screen bg-background p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="Mail" size={32} color="var(--color-primary)" />
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
              Email Notification Settings
            </h1>
          </div>
          <p className="text-muted-foreground">
            Configure automated compliance deadline alerts and violation reports for stakeholders
          </p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md mb-4 flex items-start gap-2">
            <Icon name="AlertCircle" size={18} className="mt-0.5" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {success && (
          <div className="bg-success/10 border border-success text-success px-4 py-3 rounded-md mb-4 flex items-start gap-2">
            <Icon name="CheckCircle" size={18} className="mt-0.5" />
            <span className="text-sm">{success}</span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border">
          <button
            onClick={() => setActiveTab('stakeholders')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'stakeholders' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Users" size={16} className="inline mr-2" />
            Stakeholders
          </button>
          <button
            onClick={() => setActiveTab('schedules')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'schedules' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Calendar" size={16} className="inline mr-2" />
            Scheduled Reports
          </button>
          <button
            onClick={() => setActiveTab('test')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'test' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Send" size={16} className="inline mr-2" />
            Test Email
          </button>
        </div>

        {/* Stakeholders Tab */}
        {activeTab === 'stakeholders' && (
          <div className="bg-card rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="UserPlus" size={20} />
              Add Stakeholder
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Add team members who should receive automated compliance notifications based on their role.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email Address *
                </label>
                <Input
                  type="email"
                  placeholder="stakeholder@company.com"
                  value={stakeholderEmail}
                  onChange={(e) => setStakeholderEmail(e?.target?.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Role *
                </label>
                <Select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e?.target?.value)}
                >
                  <option value="">Select Role</option>
                  {roles?.map((role) => (
                    <option key={role?.id} value={role?.id}>
                      {role?.roleName}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Department (Optional)
                </label>
                <Select
                  value={stakeholderDepartment}
                  onChange={(e) => setStakeholderDepartment(e?.target?.value)}
                >
                  <option value="">All Departments</option>
                  {departments?.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <Button
              variant="primary"
              onClick={handleAddStakeholder}
              disabled={loading}
              iconName="UserPlus"
            >
              Add Stakeholder
            </Button>

            <div className="mt-8">
              <h3 className="text-lg font-semibold text-foreground mb-4">Notification Recipients</h3>
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-2">
                  <Icon name="Info" size={14} className="inline mr-1" />
                  Stakeholders will receive emails based on their assigned roles:
                </p>
                <ul className="text-sm text-foreground space-y-1 ml-6">
                  <li>• <strong>Compliance Officer:</strong> All compliance deadline alerts and violation reports</li>
                  <li>• <strong>Payroll Manager:</strong> Payroll-related compliance deadlines (NASFUND, SWT)</li>
                  <li>• <strong>HR Manager:</strong> Employee-related compliance issues and reports</li>
                  <li>• <strong>Department Managers:</strong> Department-specific violation reports</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Scheduled Reports Tab */}
        {activeTab === 'schedules' && (
          <div className="space-y-6">
            {/* Create New Schedule */}
            <div className="bg-card rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Plus" size={20} />
                Create Scheduled Report
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Schedule Name *
                  </label>
                  <Input
                    placeholder="Weekly Compliance Report"
                    value={newSchedule?.scheduleName}
                    onChange={(e) => setNewSchedule({ ...newSchedule, scheduleName: e?.target?.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Frequency *
                  </label>
                  <Select
                    value={newSchedule?.deliveryFrequency}
                    onChange={(e) => setNewSchedule({ ...newSchedule, deliveryFrequency: e?.target?.value })}
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Report Format
                  </label>
                  <Select
                    value={newSchedule?.reportFormat}
                    onChange={(e) => setNewSchedule({ ...newSchedule, reportFormat: e?.target?.value })}
                  >
                    <option value="pdf">PDF</option>
                    <option value="excel">Excel</option>
                    <option value="csv">CSV</option>
                  </Select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Recipient Emails * (comma-separated)
                  </label>
                  <Input
                    placeholder="email1@company.com, email2@company.com"
                    value={newSchedule?.recipientEmails}
                    onChange={(e) => setNewSchedule({ ...newSchedule, recipientEmails: e?.target?.value })}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Recipient Departments (comma-separated, optional)
                  </label>
                  <Input
                    placeholder="Operations, Finance, HR"
                    value={newSchedule?.recipientDepartments}
                    onChange={(e) => setNewSchedule({ ...newSchedule, recipientDepartments: e?.target?.value })}
                  />
                </div>
              </div>

              <Button
                variant="primary"
                onClick={handleCreateSchedule}
                disabled={loading}
                iconName="Plus"
              >
                Create Schedule
              </Button>
            </div>

            {/* Existing Schedules */}
            <div className="bg-card rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="List" size={20} />
                Active Schedules
              </h2>

              {schedules?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Icon name="Calendar" size={48} className="mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No scheduled reports configured</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {schedules?.map((schedule) => (
                    <div
                      key={schedule?.id}
                      className="bg-muted/30 rounded-lg p-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-medium text-foreground">{schedule?.scheduleName}</h4>
                            <span
                              className={`text-xs px-2 py-0.5 rounded ${
                                schedule?.isActive
                                  ? 'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                              }`}
                            >
                              {schedule?.isActive ? 'Active' : 'Inactive'}
                            </span>
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded capitalize">
                              {schedule?.deliveryFrequency}
                            </span>
                          </div>

                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Icon name="Mail" size={12} />
                              <span>{schedule?.recipientEmails?.length || 0} recipients</span>
                            </div>
                            {schedule?.recipientDepartments?.length > 0 && (
                              <>
                                <span>•</span>
                                <div className="flex items-center gap-1">
                                  <Icon name="Building" size={12} />
                                  <span>{schedule?.recipientDepartments?.length} departments</span>
                                </div>
                              </>
                            )}
                            <span>•</span>
                            <div className="flex items-center gap-1">
                              <Icon name="FileText" size={12} />
                              <span>{schedule?.reportFormat?.toUpperCase()}</span>
                            </div>
                          </div>
                        </div>

                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleToggleSchedule(schedule?.id, schedule?.isActive)}
                        >
                          <Icon name={schedule?.isActive ? 'Pause' : 'Play'} size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Automated Triggers Tab */}
        {activeTab === 'test' && (
          <div className="space-y-6">
            {/* Test Email Section */}
            <div className="bg-card rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Send" size={20} />
                Send Test Email
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                Send a test email to verify your notification setup is working correctly.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Test Email Address *
                  </label>
                  <Input
                    type="email"
                    placeholder="test@company.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e?.target?.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Email Type *
                  </label>
                  <Select
                    value={testType}
                    onChange={(e) => setTestType(e?.target?.value)}
                  >
                    <option value="deadline">Compliance Deadline Alert</option>
                    <option value="violation">Violation Report</option>
                  </Select>
                </div>
              </div>

              <Button
                variant="primary"
                onClick={handleSendTestEmail}
                disabled={loading}
                iconName="Send"
              >
                Send Test Email
              </Button>

              <div className="mt-6 bg-muted/30 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Icon name="Info" size={16} />
                  Email Preview
                </h3>
                <p className="text-xs text-muted-foreground mb-2">
                  {testType === 'deadline' ?'Deadline alerts include: Alert title, due date, severity level, affected employee count, and action button to view compliance dashboard.' :'Violation reports include: Executive summary with total violations and costs, detailed violation table with severity and department breakdown, and recommended actions.'}
                </p>
                <p className="text-xs text-muted-foreground">
                  All emails are branded with Pacific RBS Ltd contact information and include direct links to the relevant dashboard sections.
                </p>
              </div>
            </div>

            {/* Automated Triggers */}
            <AutomatedEmailTriggers />
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailNotificationSettings;